import {css, cx} from "@panda/css";

import {CloseIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {RequestState} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {ApplicationFormBox} from "../../../application/components/form_box/ApplicationFormBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {RPModal} from "../../../atoms/RPModal";
import {useRegistrationWizardExposure} from "../../../auth/hooks/use_registration_wizard_exposure";
import {PropertyPlanDisplay} from "../../../property/components/PropertyPlanDisplay";
import {PropertyPlanModalContent} from "../../../property/components/PropertyPlanModalContent";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {useAppSelector} from "../../../store/hooks";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {ViewType} from "../../../view_type/ViewType";
import {GalleryModalTabs} from "../../atoms/GalleryModalTabs";
import {OfferMapModalContent} from "../../detail/components/OfferMapModalContent";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {useConstructionPhotosAccess} from "../../hooks/use_construction_photos_access";
import {IRpGalleryAmplitudeContext} from "../../tracking/amplitude/get_rp_gallery_amplitude_location";
import {useTrackAmplitudeOpenGallery} from "../../tracking/amplitude/use_track_amplitude_open_gallery";
import {IConstructionGalleryApiResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {hasOffer3dView} from "../../utils/has_offer_3d_view";
import {ConstructionGalleryPaywallTrigger} from "./ConstructionGalleryPaywallTrigger";
import {ConstructionGalleryPlaceholder} from "./ConstructionGalleryPlaceholder";
import {GalleryHolderPropertyPlanButton} from "./GalleryHolderPropertyPlanButton";
import {OfferConstructionGalleryTile} from "./OfferConstructionGalleryTile";
import {OfferGalleryModalContent} from "./OfferGalleryModalContent";

interface IProps {
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    property?: IPropertyDetail;
    galleryPictures?: IOfferGallery | null;
    propertyGalleryPictures?: IPropertyGallery | null;
    constructionGalleryResponse?: IConstructionGalleryApiResponse | null;
    offerBuildings?: IOfferBuilding[];
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    onModalClose?: () => void;
    isOpen: boolean;
    galleryFetchState: RequestState;
    propertyGalleryFetchState: RequestState;
    constructionFetchState: RequestState;
    amplitudeGalleryContext: IRpGalleryAmplitudeContext;
}

export const GalleryHolderModal = (props: IProps) => {
    const {
        offer,
        property,
        handleGalleryModalChange,
        modalState,
        onModalClose,
        offerBuildings,
        isOpen,
        source,
        constructionGalleryResponse,
        galleryPictures,
        propertyGalleryPictures,
        galleryFetchState,
        propertyGalleryFetchState,
        constructionFetchState,
        amplitudeGalleryContext
    } = props;

    const constructionGalleryPictures = constructionGalleryResponse?.gallery;
    const offer3dTours = constructionGalleryResponse?.construction_3d_tours ?? null;
    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isLimited && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property?.for_sale;
    const isMapsTabOpen = modalState === OfferModalActiveTab.MAP;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAuthenticated = useIsAuthenticated();
    const constructionPhotosAccess = useConstructionPhotosAccess();
    const hasConstructionContent = Boolean(constructionGalleryPictures?.length) || hasOffer3dView(offer, offer3dTours);
    const isConstructionPaywallShown = hasConstructionContent ? !constructionPhotosAccess.hasAccess : !isAuthenticated;
    const isConstructionContentShown = isOpen && modalState === OfferModalActiveTab.CONSTRUCTION_GALLERY && hasConstructionContent;
    const sourceSection = modalState === OfferModalActiveTab.MAP ? ApplicationSourceSection.MAP : ApplicationSourceSection.GALLERY;
    const showSidebar = (isClassicView && isPropertyForSale) || (!property && isClassicView);
    const viewType = useAppSelector((state) => state.viewType.current);
    const isOfferView = viewType === ViewType.OFFER;

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        property: property && {
            area: property.area,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };

    useTrackAmplitudeOpenGallery({
        isOpen,
        modalState,
        location: amplitudeGalleryContext.location,
        galleryFetchState,
        propertyGalleryFetchState,
        constructionFetchState,
        offer,
        property,
        galleryPictures,
        propertyGalleryPictures,
        constructionGalleryPictures,
        hasConstructionPhotosAccess: !isConstructionPaywallShown
    });

    useRegistrationWizardExposure({
        isExposed: isConstructionContentShown && constructionPhotosAccess.isLoggedInWithoutVerifiedPhone,
        isViewActive: isOpen
    });

    const handlers = {
        onClose: () => {
            onModalClose && onModalClose();
        },
        onPlanButtonClick: () => {
            showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
        }
    };

    const lastGalleryTile = constructionGalleryPictures?.length ? null : <OfferConstructionGalleryTile onButtonClick={handleGalleryModalChange} />;

    const renderModalContent = () => {
        switch (modalState) {
            case OfferModalActiveTab.PLAN:
                return (
                    property && (
                        <PropertyPlanDisplay
                            property={property}
                            vendor={offer.vendor}
                            onPlanDownloadClick={handlers.onPlanButtonClick}
                            legacyPlan={<PropertyPlanModalContent offerName={offer.name} property={property} onPlanButtonClick={handlers.onPlanButtonClick} />}
                            className={css({
                                p: 3,
                                md: {
                                    p: 0
                                }
                            })}
                            propertyId={property.id}
                            offerId={offer.id}
                        />
                    )
                );
            case OfferModalActiveTab.GALLERY:
                return (
                    <OfferGalleryModalContent
                        key="offerGallery"
                        shouldShowVideo
                        offer={offer}
                        offerGallery={galleryPictures ?? []}
                        propertyGallery={propertyGalleryPictures ?? []}
                        showMainImage
                        lastGalleryTile={lastGalleryTile}
                        propertyId={property?.id}
                        amplitudeGalleryContext={amplitudeGalleryContext}
                    />
                );
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                if (hasConstructionContent) {
                    return (
                        <OfferGalleryModalContent
                            key="offerConstructionGallery"
                            offer={offer}
                            constructionGalleryData={constructionGalleryResponse}
                            constructionEndDate={property?.construction_end_date}
                            isConstructionGalleryTab
                            showAccountPaywall={isConstructionPaywallShown}
                            needsPhoneVerification={constructionPhotosAccess.needsPhoneVerification}
                            propertyId={property?.id}
                            amplitudeGalleryContext={amplitudeGalleryContext}
                        />
                    );
                } else if (!isAuthenticated && (!constructionGalleryPictures || constructionGalleryPictures.length === 0)) {
                    return (
                        <div className={registrationTriggerWrapper}>
                            <ConstructionGalleryPaywallTrigger
                                propertyId={property?.id}
                                offerId={offer.id}
                                construction3dTours={offer3dTours}
                                needsPhoneVerification={constructionPhotosAccess.needsPhoneVerification}
                            />
                        </div>
                    );
                } else if (isAuthenticated && (!constructionGalleryPictures || constructionGalleryPictures.length === 0)) {
                    return <ConstructionGalleryPlaceholder offerId={offer.id} favouriteLocation={amplitudeGalleryContext.location} />;
                }
                break;
            case OfferModalActiveTab.MAP:
                return <OfferMapModalContent offer={offer} offerBuildings={offerBuildings} property={property} />;
            default:
                return null;
        }
    };

    // For offer view we want plural "Odkryj ceny" and for property page we want singular "Odkryj cenę"
    const applicationButtonText = isOfferView ? "Odkryj ceny" : "Odkryj cenę";

    return (
        <>
            <RPModal
                {...props}
                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.GALLERY_MODAL}
                className={cx(galleryModalStyle, isMapsTabOpen ? css({p: "0"}) : css({p: "0.5rem 0.5rem 0 0.5rem"}))}
                isOpen={isOpen}
                onModalClose={handlers.onClose}
                closeButton={(closeModal) => <CloseIcon onClick={closeModal} fill="#fff" size="2" className={modalCloseIconHolder} />}
            >
                <div className={galleryModalImmediateWrapper}>
                    <div className={cx(modalHeaderStyle, isMapsTabOpen && mapsTabOpenHeaderStyle)}>
                        <GalleryModalTabs
                            hasPlanImage={!isEmpty(property?.plan_image_pages)}
                            handleGalleryModalChange={handleGalleryModalChange}
                            modalState={modalState || undefined}
                            has3dView={hasOffer3dView(offer, offer3dTours)}
                        />
                    </div>

                    <div
                        className={cx(
                            galleryModalContent,
                            isConstructionPaywallShown && modalState === OfferModalActiveTab.CONSTRUCTION_GALLERY && forceHiddeStyle
                        )}
                    >
                        <div className={cx(galleryModalMainContent, isMapsTabOpen && mapsTabContactBarOffsetStyle)}>
                            {renderModalContent()}

                            {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                                <ContactBar
                                    className={cx(isMapsTabOpen && contactBarWithMapsTabOpenStyle)}
                                    salesOffices={offer.sales_offices}
                                    vendor={offer.vendor}
                                    trackVendor={trackedData.vendor}
                                    trackOffer={trackedData.offer}
                                    trackProperty={trackedData.property}
                                    backgroundColor="#20212d"
                                    source={source}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    offer={offer}
                                    propertyId={property?.id}
                                    propertyPrice={property?.price}
                                    buttonsSize="x-small"
                                    modalState={modalState}
                                />
                            )}
                        </div>

                        <div className={galleryModalFormHolder}>
                            <Text variant="headline_4" as="h1" className={offerInfoTextStyle} mb={2}>
                                <span className="bt">{offer.name}</span>
                                {property && ` - ${isApartment ? "Apartament" : getOfferTypeNameCapital(offer.type)} - ${property.number}`}
                            </Text>
                            <Text variant="info_txt_1" as="h2" className={cx(offerInfoTextStyle, "bt")} mb={2}>
                                {offer.address}
                            </Text>

                            {showSidebar && (
                                <div data-testid={OFFER_TEST_IDS.OFFER_VIEW.SHOW_PRICES_FROM_GALLERY}>
                                    <ApplicationFormBox
                                        customApplicationButtonText={applicationButtonText}
                                        officeData={offer.sales_offices[0]}
                                        source={source}
                                        sourceSection={sourceSection}
                                        vendorId={offer.vendor.id}
                                        offerId={offer.id}
                                        property={property}
                                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                        optionalVendorInfoData={offer.vendor}
                                        optionalOfferPhoneData={offer}
                                        formButtonText="Zapytaj dewelopera"
                                        sidebarBg="dark"
                                        modalState={modalState}
                                        amplitudeAuthPlacement={amplitudeGalleryContext.authPlacement}
                                        offerStatusStatus={offer?.status?.status}
                                    />
                                </div>
                            )}

                            {property && property.plan && (
                                <GalleryHolderPropertyPlanButton
                                    onPlanButtonClick={handlers.onPlanButtonClick}
                                    propertyPlan={property.plan}
                                    modalState={modalState}
                                    propertyId={property.id}
                                    offerId={offer.id}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </RPModal>
        </>
    );
};

const galleryModalImmediateWrapper = css({
    display: "flex",
    flex: "100%",
    flexDirection: "column",
    overflow: "hidden"
});

const mapsTabOpenHeaderStyle = css({
    md: {
        pb: 0,
        paddingLeft: 0,
        paddingRight: 0
    }
});

const galleryModalContent = css({
    flex: "1 1 100%",
    display: "flex",
    flexDirection: "row",
    overflow: "auto",
    position: "relative",

    md: {
        overflow: "hidden"
    }
});

const galleryModalMainContent = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 100%",
    minHeight: "min-content",

    md: {
        minHeight: "auto"
    }
});

const offerInfoTextStyle = css({
    color: "#fff"
});

const modalCloseIconHolder = css({
    position: "absolute",
    right: "1rem",
    top: "1rem",
    cursor: "pointer",
    md: {
        right: "3rem",
        top: "3rem"
    }
});

const galleryModalStyle = css({
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-secondary)",
    borderRadius: 0,
    md: {
        padding: 3
    }
});

const modalHeaderStyle = css({
    flex: "1 0 auto",
    color: "white",
    padding: "2rem 0 2.4rem 0",
    display: "flex",
    flexDirection: "column",
    md: {
        padding: "2rem 5rem 2.8rem 3rem",
        flexDirection: "row",
        flexWrap: "wrap"
    }
});

const galleryModalFormHolder = css({
    flexGrow: "0",
    flexShrink: "0",
    display: "none",
    md: {
        display: "flex",
        flexDirection: "column",
        flexBasis: "33rem",
        pl: 3,
        overflow: "auto"
    }
});

const contactBarWithMapsTabOpenStyle = css({
    paddingLeft: 1,
    paddingRight: 1
});

const mapsTabContactBarOffsetStyle = css({
    paddingBottom: "5.6rem",

    md: {
        paddingBottom: 0
    }
});

const registrationTriggerWrapper = css({
    position: "relative",
    h: "80%",
    w: "100%",

    md: {
        h: "100%"
    }
});

const forceHiddeStyle = css({
    overflow: "hidden !important"
});
import {css, cx} from "@panda/css";

import {CloseIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {RequestState} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {ApplicationFormBox} from "../../../application/components/form_box/ApplicationFormBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {RPModal} from "../../../atoms/RPModal";
import {useRegistrationWizardExposure} from "../../../auth/hooks/use_registration_wizard_exposure";
import {PropertyPlanDisplay} from "../../../property/components/PropertyPlanDisplay";
import {PropertyPlanModalContent} from "../../../property/components/PropertyPlanModalContent";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {useAppSelector} from "../../../store/hooks";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {ViewType} from "../../../view_type/ViewType";
import {GalleryModalTabs} from "../../atoms/GalleryModalTabs";
import {OfferMapModalContent} from "../../detail/components/OfferMapModalContent";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {useConstructionPhotosAccess} from "../../hooks/use_construction_photos_access";
import {IRpGalleryAmplitudeContext} from "../../tracking/amplitude/get_rp_gallery_amplitude_location";
import {useTrackAmplitudeOpenGallery} from "../../tracking/amplitude/use_track_amplitude_open_gallery";
import {IConstructionGalleryApiResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {hasOffer3dView} from "../../utils/has_offer_3d_view";
import {ConstructionGalleryPaywallTrigger} from "./ConstructionGalleryPaywallTrigger";
import {ConstructionGalleryPlaceholder} from "./ConstructionGalleryPlaceholder";
import {GalleryHolderPropertyPlanButton} from "./GalleryHolderPropertyPlanButton";
import {OfferConstructionGalleryTile} from "./OfferConstructionGalleryTile";
import {OfferGalleryModalContent} from "./OfferGalleryModalContent";

interface IProps {
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    property?: IPropertyDetail;
    galleryPictures?: IOfferGallery | null;
    propertyGalleryPictures?: IPropertyGallery | null;
    constructionGalleryResponse?: IConstructionGalleryApiResponse | null;
    offerBuildings?: IOfferBuilding[];
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    onModalClose?: () => void;
    isOpen: boolean;
    galleryFetchState: RequestState;
    propertyGalleryFetchState: RequestState;
    constructionFetchState: RequestState;
    amplitudeGalleryContext: IRpGalleryAmplitudeContext;
}

export const GalleryHolderModal = (props: IProps) => {
    const {
        offer,
        property,
        handleGalleryModalChange,
        modalState,
        onModalClose,
        offerBuildings,
        isOpen,
        source,
        constructionGalleryResponse,
        galleryPictures,
        propertyGalleryPictures,
        galleryFetchState,
        propertyGalleryFetchState,
        constructionFetchState,
        amplitudeGalleryContext
    } = props;

    const constructionGalleryPictures = constructionGalleryResponse?.gallery;
    const offer3dTours = constructionGalleryResponse?.construction_3d_tours ?? null;
    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isLimited && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property?.for_sale;
    const isMapsTabOpen = modalState === OfferModalActiveTab.MAP;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAuthenticated = useIsAuthenticated();
    const constructionPhotosAccess = useConstructionPhotosAccess();
    const hasConstructionContent = Boolean(constructionGalleryPictures?.length) || hasOffer3dView(offer, offer3dTours);
    const isConstructionPaywallShown = hasConstructionContent ? !constructionPhotosAccess.hasAccess : !isAuthenticated;
    const isConstructionContentShown = isOpen && modalState === OfferModalActiveTab.CONSTRUCTION_GALLERY && hasConstructionContent;
    const sourceSection = modalState === OfferModalActiveTab.MAP ? ApplicationSourceSection.MAP : ApplicationSourceSection.GALLERY;
    const showSidebar = (isClassicView && isPropertyForSale) || (!property && isClassicView);
    const viewType = useAppSelector((state) => state.viewType.current);
    const isOfferView = viewType === ViewType.OFFER;

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        property: property && {
            area: property.area,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };

    useTrackAmplitudeOpenGallery({
        isOpen,
        modalState,
        location: amplitudeGalleryContext.location,
        galleryFetchState,
        propertyGalleryFetchState,
        constructionFetchState,
        offer,
        property,
        galleryPictures,
        propertyGalleryPictures,
        constructionGalleryPictures,
        hasConstructionPhotosAccess: !isConstructionPaywallShown
    });

    useRegistrationWizardExposure({
        isExposed: isConstructionContentShown && constructionPhotosAccess.isLoggedInWithoutVerifiedPhone,
        isViewActive: isOpen
    });

    const handlers = {
        onClose: () => {
            onModalClose && onModalClose();
        },
        onPlanButtonClick: () => {
            showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
        }
    };

    const lastGalleryTile = constructionGalleryPictures?.length ? null : <OfferConstructionGalleryTile onButtonClick={handleGalleryModalChange} />;

    const renderModalContent = () => {
        switch (modalState) {
            case OfferModalActiveTab.PLAN:
                return (
                    property && (
                        <PropertyPlanDisplay
                            property={property}
                            vendor={offer.vendor}
                            onPlanDownloadClick={handlers.onPlanButtonClick}
                            legacyPlan={<PropertyPlanModalContent offerName={offer.name} property={property} onPlanButtonClick={handlers.onPlanButtonClick} />}
                            className={css({
                                p: 3,
                                md: {
                                    p: 0
                                }
                            })}
                            propertyId={property.id}
                            offerId={offer.id}
                        />
                    )
                );
            case OfferModalActiveTab.GALLERY:
                return (
                    <OfferGalleryModalContent
                        key="offerGallery"
                        shouldShowVideo
                        offer={offer}
                        offerGallery={galleryPictures ?? []}
                        propertyGallery={propertyGalleryPictures ?? []}
                        showMainImage
                        lastGalleryTile={lastGalleryTile}
                        propertyId={property?.id}
                        amplitudeGalleryContext={amplitudeGalleryContext}
                    />
                );
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                if (hasConstructionContent) {
                    return (
                        <OfferGalleryModalContent
                            key="offerConstructionGallery"
                            offer={offer}
                            constructionGalleryData={constructionGalleryResponse}
                            constructionEndDate={property?.construction_end_date}
                            isConstructionGalleryTab
                            showAccountPaywall={isConstructionPaywallShown}
                            needsPhoneVerification={constructionPhotosAccess.needsPhoneVerification}
                            propertyId={property?.id}
                            amplitudeGalleryContext={amplitudeGalleryContext}
                        />
                    );
                } else if (!isAuthenticated && (!constructionGalleryPictures || constructionGalleryPictures.length === 0)) {
                    return (
                        <div className={registrationTriggerWrapper}>
                            <ConstructionGalleryPaywallTrigger
                                propertyId={property?.id}
                                offerId={offer.id}
                                construction3dTours={offer3dTours}
                                needsPhoneVerification={constructionPhotosAccess.needsPhoneVerification}
                            />
                        </div>
                    );
                } else if (isAuthenticated && (!constructionGalleryPictures || constructionGalleryPictures.length === 0)) {
                    return <ConstructionGalleryPlaceholder offerId={offer.id} favouriteLocation={amplitudeGalleryContext.location} />;
                }
                break;
            case OfferModalActiveTab.MAP:
                return <OfferMapModalContent offer={offer} offerBuildings={offerBuildings} property={property} />;
            default:
                return null;
        }
    };

    // For offer view we want plural "Odkryj ceny" and for property page we want singular "Odkryj cenę"
    const applicationButtonText = isOfferView ? "Odkryj ceny" : "Odkryj cenę";

    return (
        <>
            <RPModal
                {...props}
                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.GALLERY_MODAL}
                className={cx(galleryModalStyle, isMapsTabOpen ? css({p: "0"}) : css({p: "0.5rem 0.5rem 0 0.5rem"}))}
                isOpen={isOpen}
                onModalClose={handlers.onClose}
                closeButton={(closeModal) => <CloseIcon onClick={closeModal} fill="#fff" size="2" className={modalCloseIconHolder} />}
            >
                <div className={galleryModalImmediateWrapper}>
                    <div className={cx(modalHeaderStyle, isMapsTabOpen && mapsTabOpenHeaderStyle)}>
                        <GalleryModalTabs
                            hasPlanImage={!isEmpty(property?.plan_image_pages)}
                            handleGalleryModalChange={handleGalleryModalChange}
                            modalState={modalState || undefined}
                            has3dView={hasOffer3dView(offer, offer3dTours)}
                        />
                    </div>

                    <div
                        className={cx(
                            galleryModalContent,
                            isConstructionPaywallShown && modalState === OfferModalActiveTab.CONSTRUCTION_GALLERY && forceHiddeStyle
                        )}
                    >
                        <div className={cx(galleryModalMainContent, isMapsTabOpen && mapsTabContactBarOffsetStyle)}>
                            {renderModalContent()}

                            {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                                <ContactBar
                                    className={cx(isMapsTabOpen && contactBarWithMapsTabOpenStyle)}
                                    salesOffices={offer.sales_offices}
                                    vendor={offer.vendor}
                                    trackVendor={trackedData.vendor}
                                    trackOffer={trackedData.offer}
                                    trackProperty={trackedData.property}
                                    backgroundColor="#20212d"
                                    source={source}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    offer={offer}
                                    propertyId={property?.id}
                                    propertyPrice={property?.price}
                                    buttonsSize="x-small"
                                    modalState={modalState}
                                />
                            )}
                        </div>

                        <div className={galleryModalFormHolder}>
                            <Text variant="headline_4" as="h1" className={offerInfoTextStyle} mb={2}>
                                <span className="bt">{offer.name}</span>
                                {property && ` - ${isApartment ? "Apartament" : getOfferTypeNameCapital(offer.type)} - ${property.number}`}
                            </Text>
                            <Text variant="info_txt_1" as="h2" className={cx(offerInfoTextStyle, "bt")} mb={2}>
                                {offer.address}
                            </Text>

                            {showSidebar && (
                                <div data-testid={OFFER_TEST_IDS.OFFER_VIEW.SHOW_PRICES_FROM_GALLERY}>
                                    <ApplicationFormBox
                                        customApplicationButtonText={applicationButtonText}
                                        officeData={offer.sales_offices[0]}
                                        source={source}
                                        sourceSection={sourceSection}
                                        vendorId={offer.vendor.id}
                                        offerId={offer.id}
                                        property={property}
                                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                        optionalVendorInfoData={offer.vendor}
                                        optionalOfferPhoneData={offer}
                                        formButtonText="Zapytaj dewelopera"
                                        sidebarBg="dark"
                                        modalState={modalState}
                                        amplitudeAuthPlacement={amplitudeGalleryContext.authPlacement}
                                        offerStatusStatus={offer?.status?.status}
                                    />
                                </div>
                            )}

                            {property && property.plan && (
                                <GalleryHolderPropertyPlanButton
                                    onPlanButtonClick={handlers.onPlanButtonClick}
                                    propertyPlan={property.plan}
                                    modalState={modalState}
                                    propertyId={property.id}
                                    offerId={offer.id}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </RPModal>
        </>
    );
};

const galleryModalImmediateWrapper = css({
    display: "flex",
    flex: "100%",
    flexDirection: "column",
    overflow: "hidden"
});

const mapsTabOpenHeaderStyle = css({
    md: {
        pb: 0,
        paddingLeft: 0,
        paddingRight: 0
    }
});

const galleryModalContent = css({
    flex: "1 1 100%",
    display: "flex",
    flexDirection: "row",
    overflow: "auto",
    position: "relative",

    md: {
        overflow: "hidden"
    }
});

const galleryModalMainContent = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 100%",
    minHeight: "min-content",

    md: {
        minHeight: "auto"
    }
});

const offerInfoTextStyle = css({
    color: "#fff"
});

const modalCloseIconHolder = css({
    position: "absolute",
    right: "1rem",
    top: "1rem",
    cursor: "pointer",
    md: {
        right: "3rem",
        top: "3rem"
    }
});

const galleryModalStyle = css({
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-secondary)",
    borderRadius: 0,
    md: {
        padding: 3
    }
});

const modalHeaderStyle = css({
    flex: "1 0 auto",
    color: "white",
    padding: "2rem 0 2.4rem 0",
    display: "flex",
    flexDirection: "column",
    md: {
        padding: "2rem 5rem 2.8rem 3rem",
        flexDirection: "row",
        flexWrap: "wrap"
    }
});

const galleryModalFormHolder = css({
    flexGrow: "0",
    flexShrink: "0",
    display: "none",
    md: {
        display: "flex",
        flexDirection: "column",
        flexBasis: "33rem",
        pl: 3,
        overflow: "auto"
    }
});

const contactBarWithMapsTabOpenStyle = css({
    paddingLeft: 1,
    paddingRight: 1
});

const mapsTabContactBarOffsetStyle = css({
    paddingBottom: "5.6rem",

    md: {
        paddingBottom: 0
    }
});

const registrationTriggerWrapper = css({
    position: "relative",
    h: "80%",
    w: "100%",

    md: {
        h: "100%"
    }
});

const forceHiddeStyle = css({
    overflow: "hidden !important"
});
